import { Link } from "react-router-dom";
import PageBanner from "../../PageBanner";
import SocialMediaSidebar from "../../SocialMediaSidebar";
import SectionHeader from "../../SectionHeader";
import aiSegregationImage from "../../../assets/images-webp/blog2.webp"; // Add your image path
import ScrollToTopButton from "../../ScrollToTopButton";
import SectionHeaderNoPDF from "../../SectionHeaderNoPDF";

const AIWasteSegregation = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="AI-Powered Waste Segregation"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Resources" },
          { label: "Blogs" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="AI-Powered Waste Segregation @ AJEEVI"
          shareUrl="https://swmecom.ajeevi.in/blog/aipowered-waste-segregation-explained"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      {/* Page content */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Summary Section with Image */}
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="lg:w-2/3">
              <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                Summary
              </h4>
              <p className="text-black mb-4 text-lg">
                AI-powered waste segregation combines machine learning, image recognition, and robotics to revolutionize waste management. It automates sorting recyclable and non-recyclable materials, drastically reducing human effort and error while improving recycling efficiency and worker safety.
              </p>
              <p className="text-black text-lg">
                The system uses trained models that identify waste types from images or sensor data and instruct robotic systems to separate them accordingly, increasing speed, consistency, and adaptability in diverse waste handling environments.
              </p>
            </div>
            <div className="lg:w-1/3">
              <img 
                src={aiSegregationImage} 
                alt="AI-powered robotic waste segregation" 
                className="w-full h-auto rounded-lg shadow-lg object-cover"
              />
              <p className="text-sm text-gray-500 mt-2 italic">
                AI robotic system sorting different waste materials on a conveyor belt
              </p>
            </div>
          </div>

          {/* Introduction Section */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Introduction
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <p className="text-black mb-4 text-lg">
                  As urban populations surge and waste generation increases exponentially, the need for smarter waste management strategies has never been more urgent. AI-powered waste segregation leverages advanced algorithms, machine learning, and computer vision to automatically identify, classify, and separate waste types at the source.
                </p>
                <p className="text-black text-lg">
                  Traditional manual segregation is labor-intensive, prone to errors, and exposes workers to hazards. AI systems with smart cameras and robotic arms can detect materials like plastic, glass, organic, and metal waste in real-time, enabling faster, safer, and more accurate segregation at scale.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#00D084]">
                <h5 className="font-bold text-lg mb-2">AI Waste Sorting Facts</h5>
                <p className="text-gray-700">
                  Modern AI systems can sort waste with over 95% accuracy at speeds of 60+ items per minute, far surpassing human capabilities.
                </p>
              </div>
            </div>
          </div>

          {/* Key Processes Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-4 text-black">
              How AI Waste Segregation Works
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-blue-500">
                  <h6 className="font-semibold text-lg mb-2">1. Image Acquisition</h6>
                  <p className="text-gray-700">
                    High-resolution cameras scan waste items on conveyor belts, capturing detailed images from multiple angles.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-green-500">
                  <h6 className="font-semibold text-lg mb-2">2. AI Processing</h6>
                  <p className="text-gray-700">
                    Deep learning models analyze images in milliseconds, classifying materials into categories with over 90% accuracy.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-purple-500">
                  <h6 className="font-semibold text-lg mb-2">3. Robotic Sorting</h6>
                  <p className="text-gray-700">
                    Robotic arms with specialized grippers pick and place items into designated bins based on AI classification.
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-red-500">
                  <h6 className="font-semibold text-lg mb-2">4. Continuous Learning</h6>
                  <p className="text-gray-700">
                    The system improves over time by learning from new waste patterns and operator feedback.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-yellow-500">
                  <h6 className="font-semibold text-lg mb-2">5. IoT Integration</h6>
                  <p className="text-gray-700">
                    Real-time data flows to central dashboards for monitoring and optimizing the entire waste stream.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border-l-4 border-indigo-500">
                  <h6 className="font-semibold text-lg mb-2">6. Quality Control</h6>
                  <p className="text-gray-700">
                    Secondary sensors verify sorted materials and provide feedback to improve accuracy.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Technology Benefits Visual */}
          <div className="mb-10 bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-4 text-black text-center">
              AI Segregation Benefits
            </h5>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">95%+</div>
                <p className="text-gray-700">Sorting accuracy rate</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">60x</div>
                <p className="text-gray-700">Faster than manual sorting</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <div className="text-3xl font-bold text-[#00D084] mb-2">30%</div>
                <p className="text-gray-700">Higher recycling yields</p>
              </div>
            </div>
          </div>

          {/* Conclusion Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">Conclusion</h5>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-2/3">
                <p className="text-black text-lg">
                  AI-powered waste segregation is transforming urban sustainability and circular economy initiatives. By eliminating manual sorting inefficiencies, reducing recyclable contamination, and enhancing material recovery rates, this technology represents the future of waste management. Continuous advancements in machine learning and robotics will make these systems even more autonomous, efficient, and eco-friendly.
                </p>
              </div>
              <div className="md:w-1/3 bg-[#00D084] bg-opacity-10 p-4 rounded-lg">
                <h6 className="font-semibold text-lg mb-2">Future Developments</h6>
                <p className="text-gray-700">
                  Next-generation systems will incorporate multi-spectral imaging and advanced robotics for even finer material separation.
                </p>
              </div>
            </div>
          </div>

          {/* Ajeevi's Contribution Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h5 className="text-xl font-semibold mb-3 text-black">
              Ajeevi's AI Waste Solutions
            </h5>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-black mb-4 text-lg">
                  Ajeevi Technologies integrates cutting-edge AI into smart city waste management frameworks, delivering innovative solutions that combine computer vision, robotics, and IoT for maximum efficiency and sustainability.
                </p>
                <p className="text-black text-lg">
                  Learn more at{" "}
                  <a
                    href="http://www.ajeevi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D084] hover:underline"
                  >
                    www.ajeevi.com
                  </a>.
                </p>
              </div>
              <div>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>
                    <strong>Smart Material Recovery:</strong> AI systems for waste identification and robotic sorting
                  </li>
                  <li>
                    <strong>Edge AI Processors:</strong> On-device processing for real-time decision making
                  </li>
                  <li>
                    <strong>Predictive Waste Analytics:</strong> Forecasting generation patterns and optimizing operations
                  </li>
                  <li>
                    <strong>Integrated Platforms:</strong> Combining AI, IoT and cloud for complete waste management
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIWasteSegregation;